import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import { useCartContext } from './CartContext'

/**
 * ProfileContext — single source for the storefront Profile overlay.
 *
 * Mirrors CartContext: owns the open/close state + mounts the overlay
 * once, globally, so any page (or another overlay like the cart) can
 * trigger it. Before this provider the overlay was mounted ad-hoc by
 * each storefront page, so the cart drawer couldn't reach it.
 *
 * Round-trip support: callers can open with `{ returnToCart: true }` —
 * after a successful save the overlay calls onSaved which closes the
 * profile and re-opens the cart. The cart's address check fires fresh
 * on re-mount and the operator goes straight to Confirm.
 *
 * Must mount INSIDE CartProvider (we call useCartContext).
 */
interface OpenOptions {
  readonly returnToCart?: boolean
}

export interface UseProfileContext {
  readonly isOpen: boolean
  readonly open: (options?: OpenOptions) => void
  readonly close: () => void
  /** Wired by the overlay host — fires after a successful save. The
   *  provider uses this to bounce back to the cart when the overlay was
   *  opened with { returnToCart: true }. Exposed on the context so the
   *  <Overlays /> host (which lives below this provider) can forward it
   *  to ProfileOverlay as a prop. */
  readonly handleSaved: () => void
}

const ProfileContext = createContext<UseProfileContext | undefined>(undefined)

export function ProfileProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const cart = useCartContext()
  const [isOpen, setIsOpen] = useState(false)
  const [returnToCart, setReturnToCart] = useState(false)

  const open = useCallback((options?: OpenOptions): void => {
    setReturnToCart(options?.returnToCart ?? false)
    setIsOpen(true)
  }, [])
  const close = useCallback((): void => {
    setIsOpen(false)
    setReturnToCart(false)
  }, [])

  const handleSaved = useCallback((): void => {
    setIsOpen(false)
    if (returnToCart) {
      setReturnToCart(false)
      // The cart's hasAddress check re-runs on mount; opening the
      // drawer is enough — no need to re-fetch from here.
      cart.open()
    }
  }, [cart, returnToCart])

  const value: UseProfileContext = { isOpen, open, close, handleSaved }

  // Like CartProvider: the overlay itself is mounted by <Overlays /> in
  // AppRouter, not here. That way the overlay can see BOTH contexts.
  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  )
}

export function useProfileContext(): UseProfileContext {
  const ctx = useContext(ProfileContext)
  if (ctx === undefined) {
    throw new Error('useProfileContext must be used inside <ProfileProvider>')
  }
  return ctx
}
